import { useLazyQuery, useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Card,
  Checkbox,
  CurrencyFlagEmoji,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import { currencyCodeToName, fxAllowedCurrencies } from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, useEffect } from "react";
import { z } from "zod";
import { BusinessEntityCard } from "~/components/BusinessEntity/BusinessEntityCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { ProductCard } from "~/components/ProductCard";
import { graphql } from "~/gql/client";
import type { Currency } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const formSchema = z
  .object({
    organisationId: z.string().min(1, "Required"),
    businessEntityId: z.string().min(1, "Required"),
    productIds: z.array(z.string()),
    currencies: z.array(z.string()),
  })
  .superRefine((val, ctx) => {
    if (val.productIds.length === 0 && val.currencies.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "Select at least one product or currency",
        path: ["productIds"],
      });
    }
  });

type FormValues = z.input<typeof formSchema>;

export const Route = createFileRoute("/__auth/onboarding-requests_/new")({
  head: () => opsHead("New Onboarding Request"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const ENTITY_STATUS_QUERY = graphql(`
  query AdminOnboardingRequestFormEntityStatus(
    $businessEntityId: ID!
    $organisationId: ID!
  ) {
    businessEntityNode: node(id: $businessEntityId) {
      ... on BusinessEntity {
        id
        assets {
          id
          currency
          ... on SavingsAsset {
            product {
              id
            }
          }
          ... on FixedIncomeFundAsset {
            product {
              id
            }
          }
          ... on TermDepositAsset {
            product {
              id
            }
          }
        }
      }
    }
    organisationNode: node(id: $organisationId) {
      ... on Organisation {
        accountOnboardingRequests(statuses: [PENDING]) {
          ... on FXOnboardingRequest {
            id
            currency
            businessEntity {
              id
            }
          }
          ... on ProductAccountOnboardingRequest {
            id
            product {
              id
            }
            businessEntity {
              id
            }
          }
          ... on MercuryOnboardingRequest {
            id
            businessEntity {
              id
            }
          }
        }
      }
    }
  }
`);

function View() {
  const navigate = useNavigate();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminNewOnboardingRequestForm {
        organisations {
          id
          name
          ...OrganisationCardFields
          businessEntities {
            id
            ...BusinessEntityCardFields
          }
        }
        products {
          id
          available
          ...ProductCardFields
        }
      }
    `),
  );

  const [createProductOnboardingRequest] = useMutation(
    graphql(`
      mutation AdminCreateProductOnboardingRequest($input: CreateProductOnboardingRequestInput!) {
        createProductOnboardingRequest(input: $input) {
          activeOrganisation {
            id
          }
        }
      }
    `),
  );

  const [createFXOnboardingRequests] = useMutation(
    graphql(`
      mutation AdminCreateFXOnboardingRequests($input: CreateFXOnboardingRequestsInput!) {
        createFXOnboardingRequests(input: $input) {
          activeOrganisation {
            id
          }
        }
      }
    `),
  );

  const [fetchEntityStatus, { data: entityStatusData }] =
    useLazyQuery(ENTITY_STATUS_QUERY);

  const sortedProducts = (data.products ?? [])
    .filter((p) => p.available)
    .sort((a, b) => a.internalName.localeCompare(b.internalName));

  const bankAccounts = sortedProducts.filter(
    (p) => p.__typename === "BankAccountProduct",
  );
  const moneyMarketFunds = sortedProducts.filter(
    (p) => p.__typename === "FundProduct",
  );
  const termDeposits = sortedProducts.filter(
    (p) => p.__typename === "TermDepositProduct",
  );

  const form = useAppForm({
    defaultValues: {
      organisationId: "",
      businessEntityId: "",
      productIds: [],
      currencies: [],
    } as FormValues,
    validators: { onSubmit: formSchema },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Please review the form",
        });
        return;
      }

      const { businessEntityId, productIds, currencies } = parsed.data;

      const mutations: Promise<unknown>[] = [];

      if (productIds.length > 0) {
        mutations.push(
          new Promise<void>((resolve, reject) => {
            createProductOnboardingRequest({
              variables: {
                input: {
                  productRequests: productIds.map((productId) => ({
                    productId,
                  })),
                  businessEntityId,
                },
              },
              onCompleted: () => resolve(),
              onError: (error) => reject(error),
            });
          }),
        );
      }

      if (currencies.length > 0) {
        mutations.push(
          new Promise<void>((resolve, reject) => {
            createFXOnboardingRequests({
              variables: {
                input: {
                  currencyRequests: currencies.map((c) => ({
                    currency: c as Currency,
                  })),
                  businessEntityId,
                },
              },
              onCompleted: () => resolve(),
              onError: (error) => reject(error),
            });
          }),
        );
      }

      try {
        await Promise.all(mutations);
        toast({
          kind: "success",
          autoHide: true,
          title: "Onboarding request created",
        });
        void navigate({ to: "/onboarding-requests" });
      } catch (error) {
        const graphQLError =
          error instanceof Error ? error : new Error("Unknown error");
        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating onboarding request",
          description: graphQLError.message,
        });
      }
    },
  });

  const selectedOrganisationId = useStore(
    form.store,
    (state) => state.values.organisationId,
  );
  const selectedBusinessEntityId = useStore(
    form.store,
    (state) => state.values.businessEntityId,
  );
  const productIds = useStore(form.store, (state) => state.values.productIds);
  const currencies = useStore(form.store, (state) => state.values.currencies);

  const selectionDisabled =
    !selectedOrganisationId || !selectedBusinessEntityId;

  const selectedOrganisation = data.organisations?.find(
    (o) => o.id === selectedOrganisationId,
  );

  useEffect(() => {
    if (
      selectedOrganisation?.businessEntities?.length === 1 &&
      selectedOrganisation.businessEntities[0]
    ) {
      form.setFieldValue(
        "businessEntityId",
        selectedOrganisation.businessEntities[0].id,
      );
    }
  }, [form, selectedOrganisation]);

  // Compute existing asset product IDs and pending requests for the selected entity
  const existingAssetProductIds = new Set<string>();
  const existingFxCurrencies = new Set<string>();
  const pendingProductIds = new Set<string>();
  const pendingFxCurrencies = new Set<string>();

  if (selectedBusinessEntityId && entityStatusData) {
    const beNode = entityStatusData.businessEntityNode;
    if (beNode?.__typename === "BusinessEntity") {
      for (const asset of beNode.assets ?? []) {
        if (asset.__typename === "FXAsset") {
          existingFxCurrencies.add(asset.currency);
        } else if (
          (asset.__typename === "SavingsAsset" ||
            asset.__typename === "FixedIncomeFundAsset" ||
            asset.__typename === "TermDepositAsset") &&
          asset.product?.id
        ) {
          existingAssetProductIds.add(asset.product.id);
        }
      }
    }

    const orgNode = entityStatusData.organisationNode;
    if (orgNode?.__typename === "Organisation") {
      for (const req of orgNode.accountOnboardingRequests ?? []) {
        if (req.businessEntity?.id !== selectedBusinessEntityId) {
          continue;
        }
        if (req.__typename === "FXOnboardingRequest") {
          pendingFxCurrencies.add(req.currency);
        } else if (
          req.__typename === "ProductAccountOnboardingRequest" &&
          req.product?.id
        ) {
          pendingProductIds.add(req.product.id);
        }
      }
    }
  }

  const getProductStatus = (
    productId: string,
  ): "asset-exists" | "pending" | null => {
    if (existingAssetProductIds.has(productId)) {
      return "asset-exists";
    }
    if (pendingProductIds.has(productId)) {
      return "pending";
    }
    return null;
  };

  const getCurrencyStatus = (
    currency: string,
  ): "asset-exists" | "pending" | null => {
    if (existingFxCurrencies.has(currency)) {
      return "asset-exists";
    }
    if (pendingFxCurrencies.has(currency)) {
      return "pending";
    }
    return null;
  };

  return (
    <AdminPageContainer testId="admin-onboarding-requests-new">
      <AdminPageHeader
        title="New Onboarding Request"
        subtitle="Create an account onboarding request for a client"
      />
      <Card variant="panel">
        <form.AppForm>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
            className="space-y-6"
          >
            <form.AppField name="organisationId">
              {(field) => (
                <field.SelectField
                  label="Organisation"
                  placeholder="Select an organisation..."
                  onValueChange={(e) => {
                    field.handleChange(e);
                    field.form.setFieldValue("businessEntityId", "");
                  }}
                >
                  {(data.organisations ?? [])
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((o) => (
                      <field.SelectItem key={o.id} value={o.id}>
                        <OrganisationCard organisation={o} />
                      </field.SelectItem>
                    ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="businessEntityId">
              {(field) => (
                <field.SelectField
                  label="Business Entity"
                  placeholder={
                    selectedOrganisation === undefined
                      ? "Select organisation first"
                      : "Select a business entity..."
                  }
                  disabled={selectedOrganisation === undefined}
                  onValueChange={(value) => {
                    field.handleChange(value);
                    if (value && selectedOrganisationId) {
                      fetchEntityStatus({
                        variables: {
                          businessEntityId: value,
                          organisationId: selectedOrganisationId,
                        },
                      });
                    }
                  }}
                >
                  {(selectedOrganisation?.businessEntities ?? [])
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((be) => (
                      <field.SelectItem key={be.id} value={be.id}>
                        <BusinessEntityCard businessEntity={be} />
                      </field.SelectItem>
                    ))}
                </field.SelectField>
              )}
            </form.AppField>

            <div className="flex flex-col gap-2">
              <Txt variant="detail-label">Bank accounts</Txt>
              <div className="divide-y divide-grid overflow-hidden rounded-md border border-grid">
                {bankAccounts.map((product) => {
                  const status = getProductStatus(product.id);
                  return (
                    <ProductCheckbox
                      key={product.id}
                      product={product}
                      checked={productIds.includes(product.id)}
                      disabled={selectionDisabled || status !== null}
                      status={status}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          form.setFieldValue("productIds", [
                            ...productIds,
                            product.id,
                          ]);
                        } else {
                          form.setFieldValue(
                            "productIds",
                            productIds.filter((id) => id !== product.id),
                          );
                        }
                      }}
                    />
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Txt variant="detail-label">Money market funds</Txt>
              <div className="divide-y divide-grid overflow-hidden rounded-md border border-grid">
                {moneyMarketFunds.map((product) => {
                  const status = getProductStatus(product.id);
                  return (
                    <ProductCheckbox
                      key={product.id}
                      product={product}
                      checked={productIds.includes(product.id)}
                      disabled={selectionDisabled || status !== null}
                      status={status}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          form.setFieldValue("productIds", [
                            ...productIds,
                            product.id,
                          ]);
                        } else {
                          form.setFieldValue(
                            "productIds",
                            productIds.filter((id) => id !== product.id),
                          );
                        }
                      }}
                    />
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Txt variant="detail-label">Term deposits</Txt>
              <div className="divide-y divide-grid overflow-hidden rounded-md border border-grid">
                {termDeposits.map((product) => {
                  const status = getProductStatus(product.id);
                  return (
                    <ProductCheckbox
                      key={product.id}
                      product={product}
                      checked={productIds.includes(product.id)}
                      disabled={selectionDisabled || status !== null}
                      status={status}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          form.setFieldValue("productIds", [
                            ...productIds,
                            product.id,
                          ]);
                        } else {
                          form.setFieldValue(
                            "productIds",
                            productIds.filter((id) => id !== product.id),
                          );
                        }
                      }}
                    />
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Txt variant="detail-label">FX accounts</Txt>
              <div className="divide-y divide-grid overflow-hidden rounded-md border border-grid">
                {fxAllowedCurrencies.map((currency) => {
                  const checked = currencies.includes(currency);
                  const status = getCurrencyStatus(currency);
                  const isDisabled = selectionDisabled || status !== null;
                  return (
                    <label
                      key={currency}
                      className={`flex items-center gap-3 p-3 ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-secondary"}`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={isDisabled}
                        onCheckedChange={(value) => {
                          if (value) {
                            form.setFieldValue("currencies", [
                              ...currencies,
                              currency,
                            ]);
                          } else {
                            form.setFieldValue(
                              "currencies",
                              currencies.filter((c) => c !== currency),
                            );
                          }
                        }}
                      />
                      <CurrencyFlagEmoji
                        currency={currency}
                        className="h-5 w-7 rounded-sm text-[20px] shadow-surface"
                      />
                      <div className="flex-1">
                        <Txt variant="body">
                          {currency} {currencyCodeToName[currency]}
                        </Txt>
                      </div>
                      {status !== null && (
                        <Txt variant="caption" tone="secondary">
                          {status === "asset-exists"
                            ? "Asset already exists"
                            : "Already requested"}
                        </Txt>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end">
              <form.SubmitButton
                childrenWhileSubmitting="Creating..."
                disabled={form.state.isSubmitting}
              >
                Create
              </form.SubmitButton>
            </div>
          </form>
        </form.AppForm>
      </Card>
    </AdminPageContainer>
  );
}

function ProductCheckbox({
  product,
  checked,
  disabled,
  status,
  onCheckedChange,
}: {
  product: Parameters<typeof ProductCard>[0]["product"];
  checked: boolean;
  disabled: boolean;
  status: "asset-exists" | "pending" | null;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={`flex items-center gap-3 p-2 ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-secondary"}`}
    >
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
      <div className="flex-1">
        <ProductCard product={product} />
      </div>
      {status !== null && (
        <Txt variant="caption" tone="secondary">
          {status === "asset-exists"
            ? "Asset already exists"
            : "Already requested"}
        </Txt>
      )}
    </label>
  );
}
